import { Text } from '@loomhq/lens';
import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { getBoundPosition } from 'react-draggable/build/cjs/utils/positionFns';
import { useSelector } from 'react-redux';

import { SMALL_TABLET_MIN_WIDTH } from 'constants/breakpoints';
import { CAPTIONS_DRAG_END } from 'constants/events';
import * as analytics from 'utilities/analytics';

import useMedia from 'hooks/useMedia';
import { selectCommentBuckets } from 'selectors/comments';
import {
  selectControlBarExpanded,
  selectCurrentVideoId,
} from 'selectors/videoPlayer';

import { useShouldShowWaveform } from 'components/share-video/trim-tools/hooks';

import styles from './styles.module.less';

// WebVTT has several tags in it that we do not want to show on the
// screen. The main one is to differeniate voices, a feature we do not
// have.
const stripTags = caption => caption.replace(/<\/?[\s\d\w:\.]+>/g, '');

const Captions = ({ text, videoWidth, videoHeight }) => {
  const videoId = useSelector(selectCurrentVideoId);
  const controlBarExpanded = useSelector(selectControlBarExpanded);

  const commentBuckets = useSelector(selectCommentBuckets);
  const activeBucketIdx = useSelector(
    state => state.video_player.active_comment_bucket_index
  );
  const hoveredBucketIdx = useSelector(
    state => state.video_player.hovered_comment_bucket_index
  );
  const isShowingComment =
    activeBucketIdx in commentBuckets || hoveredBucketIdx !== -1;

  const shouldShowWaveform = useShouldShowWaveform();

  const shouldShowCC = useSelector(
    state =>
      state.video_player.has_started &&
      !state.video_player.ended &&
      (!state.trimming.in_trim_mode || shouldShowWaveform)
  );

  const meetsMinWidthForComments = useMedia(
    [`(min-width: ${SMALL_TABLET_MIN_WIDTH}px)`],
    [true],
    false
  );

  const captionsRef = useRef();
  const draggableRef = useRef();

  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const [dragging, setDragging] = useState(false);

  // We want to scale our font size based on the width of the video.
  const [fontSize, setFontSize] = useState(0);

  useEffect(() => {
    setFontSize(Math.max(12, Math.floor(videoWidth / 50)));
  }, [videoWidth]);

  // The width and height of the captions has to be measured to position them
  // correctly.
  let [captionsWidth, captionsHeight] = [0, 0];

  if (captionsRef.current) {
    const dimensions = captionsRef.current.getBoundingClientRect();

    captionsWidth = dimensions.width;
    captionsHeight = dimensions.height;
  }

  // Make sure the captions are positioned correctly whenever they change
  // text, the video width changes, or they need to be moved up b/c of a
  // comment.
  //
  // The bottom position of captions changes if the control bar is expanded
  // and we are currently showing a comment. This behavior stops after the
  // captions have been dragged by the user.
  let distanceFromBottomPx;

  if (meetsMinWidthForComments) {
    distanceFromBottomPx = 88;

    if (isShowingComment && controlBarExpanded) {
      distanceFromBottomPx += 64;
    }
  } else {
    distanceFromBottomPx = 32;

    if (controlBarExpanded) {
      distanceFromBottomPx += 56;
    }
  }

  const yPosPx = videoHeight - distanceFromBottomPx - captionsHeight;

  // When we move the captions, we want to make sure they do not fall outside
  // the bounds we've set up for them. Our `setCaptionPos` function calls
  // `getBoundPosition` from react-draggable to ensure that.
  const [captionPos, _setCaptionPos] = useState({
    x: 0,
    y: 0,
  });
  const setCaptionPos = useCallback(
    ({ x, y }) => {
      if (draggableRef.current) {
        [x, y] = getBoundPosition(draggableRef.current, x, y);

        if (captionPos.x !== x || captionPos.y !== y) {
          _setCaptionPos({
            x,
            y,
          });
        }
      }
    },
    [captionPos.x, captionPos.y]
  );

  const resetCaptionPos = useCallback(() => {
    const x = Math.floor((videoWidth - captionsWidth) / 2);
    const y = yPosPx;

    setCaptionPos({
      x,
      y,
    });
    setHasBeenDragged(false);
  }, [videoWidth, captionsWidth, yPosPx, setCaptionPos]);

  useEffect(() => {
    if (hasBeenDragged) {
      return;
    }

    resetCaptionPos();
  }, [resetCaptionPos, hasBeenDragged]);

  // When the video size changes, move the captions intelligently and ensure
  // they are in bounds
  useEffect(() => {
    setCaptionPos({
      x: captionPos.x,
      y: captionPos.y,
    });
  }, [captionPos.x, captionPos.y, setCaptionPos, videoWidth, text]);

  const [lastVideoWidth, setLastVideoWidth] = useState(0);

  // Reset captions to their natural starting point when we reduce size below
  // 768px for the player width. This prevents resizing weirdness.
  const resetCaptionsBreakpoint = 768;

  useEffect(() => {
    if (
      lastVideoWidth > resetCaptionsBreakpoint &&
      videoWidth <= resetCaptionsBreakpoint
    ) {
      resetCaptionPos();
    }

    setLastVideoWidth(videoWidth);
  }, [videoWidth, lastVideoWidth, resetCaptionPos]);

  if (!shouldShowCC) {
    return null;
  }

  // We have to show and hide this instead of just removing it from the DOM
  // so that its dragged position is saved. If we remove it from the DOM,
  // it will return to its original position.
  if (text) {
    text = stripTags(text);
  }

  const shouldDisplay = text?.length && text.length > 0;

  return (
    <div className={classnames(styles.dragWrapper, 'dragWrapper')}>
      <div
        style={{
          display: shouldDisplay ? 'block' : 'none',
        }}
      >
        <Draggable
          ref={draggableRef}
          bounds=".dragWrapper"
          position={captionPos}
          onStart={() => {
            setHasBeenDragged(true);
            setDragging(true);
          }}
          onStop={(event, data) => {
            const { x, y } = data;

            analytics.track(CAPTIONS_DRAG_END, {
              videoId,
              x,
              y,
            });
            setDragging(false);
            setCaptionPos({ x, y });
          }}
        >
          <div
            ref={captionsRef}
            className={styles.captions}
            style={{
              left: 0,
              top: 0,
              cursor: dragging ? 'grabbing' : 'grab',
            }}
          >
            <Text
              isInline={true}
              style={{
                fontSize: `${fontSize}px`,
              }}
            >
              {text}
            </Text>
          </div>
        </Draggable>
      </div>
    </div>
  );
};

export default Captions;
